import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Award,
  Phone,
  Globe,
  Mail,
  MapPin,
  Calendar,
  Building2,
  Home,
  Star,
  User,
  Shield,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "../services/apiClient";
import { formatPropertyPrice } from "../utils/formatters";

const BrokerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [broker, setBroker] = useState(null);
  const [managedProperties, setManagedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    property_type: "",
    budget_range: "",
  });

  useEffect(() => {
    fetchBroker();
    fetchManagedProperties();
  }, [id]);

  const fetchBroker = async () => {
    try {
      const response = await api.get(`/brokers/${id}`, { auth: false });
      const data = await response.json();
      setBroker(data);
    } catch (error) {
      console.error("Error fetching broker:", error);
      toast.error("Error al cargar perfil del broker");
    } finally {
      setLoading(false);
    }
  };

  const fetchManagedProperties = async () => {
    try {
      const response = await api.get(`/brokers/${id}/properties`, {
        auth: false,
      });
      if (response.ok) {
        const data = await response.json();
        setManagedProperties(data);
      }
    } catch (error) {
      console.error("Error fetching managed properties:", error);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await api.post(`/brokers/${id}/contact`, {
        ...contactForm,
        broker_id: id,
      });

      if (!response.ok) throw new Error("Error al enviar consulta");

      toast.success("¡Mensaje enviado al broker!");
      setContactForm({
        name: "",
        email: "",
        phone: "",
        message: "",
        property_type: "",
        budget_range: "",
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!broker) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="flex items-center justify-center py-20">
          <p className="text-lg text-muted-foreground">Broker no encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Profile Image */}
            {broker.image_url ? (
              <img
                src={broker.image_url}
                alt={broker.name}
                className="w-36 h-36 rounded-full object-cover border-4 border-white shadow-xl"
              />
            ) : (
              <div className="w-36 h-36 rounded-full bg-gradient-to-br from-primary/30 to-primary/10 flex items-center justify-center border-4 border-white shadow-xl">
                <User className="w-16 h-16 text-primary" />
              </div>
            )}

            {/* Broker Info */}
            <div className="text-center md:text-left flex-1">
              <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
                <h1
                  className="text-3xl md:text-4xl font-bold tracking-tight"
                  style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
                >
                  {broker.name}
                </h1>
                {broker.is_verified && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
                    <Shield className="w-4 h-4" />
                    Verificado
                  </span>
                )}
              </div>

              {/* Quick Stats */}
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-muted-foreground mb-4">
                {broker.years_experience > 0 && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {broker.years_experience} años de experiencia
                  </span>
                )}
                {broker.total_listings > 0 && (
                  <span className="flex items-center gap-1">
                    <Building2 className="w-4 h-4" />
                    {broker.total_listings} propiedades
                  </span>
                )}
                {broker.rating > 0 && (
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500" />
                    {broker.rating.toFixed(1)}
                  </span>
                )}
              </div>

              {/* Service Areas */}
              {broker.service_areas && broker.service_areas.length > 0 && (
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    Zonas de cobertura:
                  </span>
                  {broker.service_areas.map((area, idx) => (
                    <span
                      key={idx}
                      className="px-2 py-1 bg-white/80 text-xs rounded-full"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - About */}
          <div className="lg:col-span-2 space-y-8">
            {/* About Section */}
            <div>
              <h2
                className="text-2xl font-bold mb-4"
                style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              >
                Acerca del Broker
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {broker.description ||
                  "Este broker aún no ha agregado una descripción."}
              </p>
            </div>

            {/* Specialties */}
            {broker.specialties && broker.specialties.length > 0 && (
              <div>
                <h3 className="text-xl font-bold mb-4">Especialidades</h3>
                <div className="flex flex-wrap gap-2">
                  {broker.specialties.map((specialty, idx) => (
                    <span
                      key={idx}
                      className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium"
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Certifications */}
            {broker.certifications && broker.certifications.length > 0 && (
              <div>
                <h3 className="text-xl font-bold mb-4">Certificaciones</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {broker.certifications.map((cert, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-3 p-3 bg-muted rounded-lg"
                    >
                      <Award className="w-5 h-5 text-amber-600 flex-shrink-0" />
                      <span className="text-sm">{cert}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Portfolio - Managed Properties */}
            {managedProperties.length > 0 && (
              <div>
                <h3 className="text-xl font-bold mb-4">
                  Propiedades que Representa ({managedProperties.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {managedProperties.slice(0, 6).map((property) => (
                    <div
                      key={property.id}
                      className="border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/property/${property.id}`)}
                    >
                      {property.images && property.images[0] ? (
                        <img
                          src={property.images[0]}
                          alt={property.title}
                          className="w-full h-40 object-cover"
                        />
                      ) : (
                        <div className="w-full h-40 bg-muted flex items-center justify-center">
                          <Home className="w-12 h-12 text-muted-foreground" />
                        </div>
                      )}
                      <div className="p-4">
                        <h4 className="font-semibold line-clamp-1 mb-1">
                          {property.title}
                        </h4>
                        <p className="text-sm text-muted-foreground line-clamp-1 mb-2">
                          {property.address || property.city}
                        </p>
                        <p className="text-lg font-bold text-primary">
                          {formatPropertyPrice(property)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                {managedProperties.length > 6 && (
                  <div className="text-center mt-4">
                    <Button
                      variant="outline"
                      onClick={() => navigate(`/search?broker=${id}`)}
                    >
                      Ver todas las propiedades ({managedProperties.length})
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Empty Portfolio State */}
            {managedProperties.length === 0 && (
              <div className="text-center py-8 bg-muted/50 rounded-lg">
                <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  Este broker aún no tiene propiedades listadas en la
                  plataforma.
                </p>
              </div>
            )}
          </div>

          {/* Right Column - Contact Form */}
          <div>
            <div className="bg-muted rounded-lg p-6 sticky top-24">
              <h3 className="text-xl font-bold mb-2">
                Contactar a {broker.name?.split(" ")[0]}
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Envía un mensaje a través de la plataforma
              </p>

              <form onSubmit={handleContactSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Nombre *
                  </label>
                  <input
                    type="text"
                    value={contactForm.name}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, name: e.target.value })
                    }
                    className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={contactForm.email}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, email: e.target.value })
                    }
                    className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Celular/WhatsApp
                  </label>
                  <input
                    type="tel"
                    value={contactForm.phone}
                    onChange={(e) =>
                      setContactForm({ ...contactForm, phone: e.target.value })
                    }
                    className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                    placeholder="+52 81 1234 5678"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Tipo de Propiedad
                  </label>
                  <select
                    value={contactForm.property_type}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        property_type: e.target.value,
                      })
                    }
                    className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                  >
                    <option value="">Selecciona tipo</option>
                    <option value="Residencial">Residencial</option>
                    <option value="Comercial">Comercial</option>
                    <option value="Industrial">Industrial</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Presupuesto
                  </label>
                  <select
                    value={contactForm.budget_range}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        budget_range: e.target.value,
                      })
                    }
                    className="w-full h-10 px-3 border border-input bg-background rounded-md text-sm"
                  >
                    <option value="">Selecciona rango</option>
                    <option value="<1M">Menos de $1M</option>
                    <option value="1M-3M">$1M - $3M</option>
                    <option value="3M-5M">$3M - $5M</option>
                    <option value="5M-10M">$5M - $10M</option>
                    <option value="10M-25M">$10M - $25M</option>
                    <option value="25M-50M">$25M - $50M</option>
                    <option value=">50M">Más de $50M</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Mensaje *
                  </label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) =>
                      setContactForm({
                        ...contactForm,
                        message: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 border border-input bg-background rounded-md text-sm"
                    rows={4}
                    placeholder="Cuéntale al broker qué estás buscando..."
                    required
                  />
                </div>
                <Button type="submit" className="w-full">
                  Enviar Mensaje
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BrokerProfile;
